import { Box, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { Button, ButtonIcon, ButtonText } from '@morressier/alembic';
import { IGetRevisionHistory } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import CheckList from './CheckList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { openFile } from 'src/utils/file';
import { endStates, ICheckResultResponse, IEndState } from '@publishing-engine/domain/models';
import { useState } from 'react';
import RunChecksModal from './RunChecksModal';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';
import StatusDot from './StatusDot';
import DownloadReport from './DownloadReport';
type IProps = {
  Integrity: ICheckResultResponse;
  revisionHistory?: IGetRevisionHistory | undefined;
  IsLoading: boolean;
  refetch: () => void;
  submissionContextId: string;
  currentState: string;
  latestIntegrity?: boolean;
};

export const CheckResults = ({
  Integrity,
  revisionHistory,
  IsLoading,
  refetch,
  submissionContextId,
  currentState,
  latestIntegrity
}: IProps) => {
  const [runCheckOpen, setRunCheckOpen] = useState(false);
  const onClose = () => {
    setRunCheckOpen(false);
  };
  const runchecksEnable = Integrity?.checkResults?.some(
    item =>
      ((item?.status === 'PROCESSING' && !item?.externalId) ||
        item?.status === 'NOT_STARTED') &&
      item?.runType === 'manual'
  );
  const ProcessAndCompletedChecks = Integrity?.checkResults?.filter(
    item =>
      !(
        (item?.status === 'PROCESSING' && !item?.externalId) ||
        item?.status === 'NOT_STARTED'
      )
  );
  const isEndStates = endStates.includes(
      currentState as IEndState
    );
  const refreshButtonEnable = Integrity?.checkResults?.some(
    item => item?.status === 'PROCESSING' && item?.externalId
  );
  const disableDownloadReport =
    refreshButtonEnable || ProcessAndCompletedChecks?.length === 0;

  return (
    <>
      <Flex bgColor="surfacePrimaryFill" flexDirection="column">
        {revisionHistory?.manuscript && (
          <Flex flexDirection="column" gap={4} padding={4}>
            <Text textStyle="headingItem">Reviewed Manuscript</Text>
            <Flex
              justifyContent="space-between"
              key={revisionHistory.manuscript.id}
            >
              <Flex justifyContent="space-between" width="100%" padding={4}>
                <Flex gap={2} alignItems="center">
                  <Flex>
                    <FontAwesomeIcon icon={faFile} fontSize={24} />
                  </Flex>
                  <Text textStyle="textBody">
                    {revisionHistory.manuscript.name}
                  </Text>
                </Flex>
                {revisionHistory?.manuscript?.url && (
                  <ButtonText
                    onClick={() =>
                      openFile(revisionHistory.manuscript?.url as string)
                    }
                  >
                    View
                  </ButtonText>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        <Flex
          padding={4}
          borderBottom="1px"
          alignItems="center"
          justifyContent="space-between"
          borderBottomColor="neutralPrimaryBorder"
        >
          <Flex alignItems={'center'} gap={2}>
            <Text textStyle="headingItem">
              Integrity Overview{' '}
              {Integrity.roundId === '0'
                ? '(Original Submission)'
                : '(Revision' + ' - ' + Integrity.roundId + ')'}
              {'  '}
            </Text>{' '}
            {revisionHistory && (
              <StatusDot
                status={Integrity?.overallStatus}
                isRevisionRound={true}
              />
            )}
            <DownloadReport
              submissionContextId={submissionContextId}
              submissionId={Integrity.submissionId}
              currentState={currentState}
              runId={Integrity.checkResults[0]?.runId!}
              disable={disableDownloadReport}
            />
            {/* <HStack align="center" marginTop={2}>
              <Text textStyle="textBody"></Text>
               <Text textStyle="textBody">
                Issues:{' '}
                <Badge variant="caution" fontSize="0.9em">
                  {Integrity?.failedCheckCount}
                </Badge>
              </Text>
              <Text textStyle="textBody" fontSize={'md'}>
                pending:{' '}
                <Badge variant="attention" fontSize="0.9em">
                  {Integrity?.checkCount - ProcessAndCompletedChecks.length}
                </Badge>
              </Text> 
            </HStack> */}
          </Flex>

          <Flex gap={3} alignItems={'center'}>
            {refreshButtonEnable && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  refetch();
                }}
              >
                {IsLoading ? <Spinner /> : 'Refresh'}
              </Button>
            )}

            {!isEndStates && latestIntegrity && runchecksEnable && (
              <>
                <ProtectedActionButton
                  relation="decision_maker"
                  entityType="journal_phase"
                  entityId={`${submissionContextId}_${currentState}`}
                  size="context"
                  variant="primary"
                  as="button"
                  disabled={refreshButtonEnable}
                  onClick={() => setRunCheckOpen(true)}
                >
                  {'Run checks'}
                </ProtectedActionButton>
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label={
                    <Box paddingX={6} paddingY={1}>
                      <Box> Total Checks : {Integrity?.checkCount} </Box>
                      <Box> Failed Checks : {Integrity?.failedCheckCount}</Box>
                      <Box>
                        {' '}
                        Remaining Checks :{' '}
                        {Integrity?.checkCount -
                          ProcessAndCompletedChecks.length}{' '}
                      </Box>
                    </Box>
                  }
                  borderRadius={4}
                  data-cy="contributions-info-tooltip"
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel="Back to workspace settings"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    data-cy="contributions-info-icon"
                  />
                </Tooltip>
              </>
            )}
          </Flex>
        </Flex>
        {ProcessAndCompletedChecks.length >= 1 && (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            gap={6}
            marginTop={4}
          >
            <CheckList
              roundId={Integrity?.roundId}
              checks={ProcessAndCompletedChecks}
              submissionId={Integrity.submissionId}
              submissionContextId={submissionContextId}
              currentState={currentState}
            ></CheckList>
          </Flex>
        )}{' '}
      </Flex>

      <RunChecksModal
        Integrity={Integrity}
        runCheckOpen={runCheckOpen}
        onClose={onClose}
      />
    </>
  );
};
